import { Label } from "@fluentui/react";
import { FieldContainer } from "components/controls/FieldContainer";
import { RefContext } from "contexts/RefProvider";
import { useField } from "formik";
import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Select, { mergeStyles } from "react-select";

import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import { VSpace } from "../../components/Spacer";
import { InsurerTaxonomyFieldConfig } from "../../components/form/types";
import { useDynamicConfig } from "../../components/form/useDynamicConfig";
import { ErrorMessage } from "../../components/styled";
import { useSelector } from "../../store/hooks";
import { getSelectStyles } from "../../utils/utils";

type Props = { fieldConfig: InsurerTaxonomyFieldConfig };

export const InsurerTaxonomyField = ({ fieldConfig }: Props) => {
  const config = {
    ...fieldConfig,
    ...useDynamicConfig(fieldConfig.d),
  };
  const {
    name = "",
    label,
    isVisible = true,
    isDisabled,
    isRequired,
    isSearchable,
    validate,
    include,
    renderField,
    background,
    isSortedAlphabetically,
  } = config;
  const [field, { error }, { setValue }] = useField({
    name,
    validate,
  });

  const insurerList = useSelector((s) => s.fleetForm.insurerList);
  const { setRef } = useContext(RefContext);
  const { t } = useTranslation();

  const theme = useTheme() as IStyledTheme;
  const isDarkMode = theme.isDark;

  const options = useMemo(() => {
    const options = insurerList
      ? insurerList
          .filter((tax) => {
            if (include) {
              return (include as any[]).find(
                (ins) => ins.value === tax.insurerInternalNumber
              )
                ? true
                : false;
            }
            //
            else {
              return true;
            }
          })
          .map((tax) => {
            return {
              value: tax.insurerInternalNumber,
              label: tax.insurerName + " (" + tax.insurerInternalNumber + ")",
            };
          })
      : [];

    if (isSortedAlphabetically) {
      options.sort(function (a, b) {
        var textA = a.label.toUpperCase();
        var textB = b.label.toUpperCase();
        return textA < textB ? -1 : textA > textB ? 1 : 0;
      });
    }

    return options;
  }, [t, insurerList, include, isSortedAlphabetically]);

  if (!isVisible) return null;

  const fieldRender = (
    <Select
      menuPosition="fixed"
      id={name}
      options={options as any}
      isSearchable={isSearchable}
      {...field}
      onChange={(value) => {
        setValue(value);
      }}
      styles={mergeStyles(getSelectStyles(!!error, theme), {
        menuPortal: (p) => ({
          ...p,
          background: theme.palette.neutralLighter,
          zIndex: 9999999,
        }),
        menu: (p) => ({
          ...p,
          zIndex: 9999,
          background: theme.palette.neutralLighter,
        }),
        control: (p, state) => {
          return {
            ...p,

            background: state.isDisabled ? "transparent" : theme.palette.white,

            borderBottom: error
              ? `2px solid rgb(164, 38, 44) !important`
              : state.isFocused
              ? `2px solid rgb(0, 90, 161) !important`
              : `1px solid ${theme.palette.black} !important`,
            boxShadow: "none",
            ...(state.isDisabled && {
              borderBottom: `1px solid ${theme.palette.neutralLighter} !important`,
            }),
          };
        },
        option: (p, state) => ({
          ...p,
          background:
            state.isSelected || state.isFocused
              ? theme.palette.neutralLighter
              : theme.palette.white,
          "&:hover": {
            background: theme.palette.neutralLighter,
          },
          color: theme.palette.black,
          fontSize: "14px",
          fontWeight: 400,
          wordWrap: "break-word",
        }),
        input: (p) => ({
          ...p,
          color: (function () {
            if (isDisabled) {
              return theme.palette.neutralSecondaryAlt;
            }
            if (isDarkMode) {
              return "white";
            }
            return theme.palette.black;
          })(),
        }),
        singleValue: (p, state) => ({
          ...p,
          color: theme.isDark
            ? "white"
            : isDisabled
            ? theme.palette.neutralSecondaryAlt
            : "black",
          fontSize: "14px",
          fontWeight: 400,
        }),
        dropdownIndicator: (p, state) => ({
          ...p,
          ...(state.isDisabled && { visibility: "hidden" }),
        }),
        indicatorSeparator: (p, state) => ({
          ...p,
          ...(state.isDisabled && { visibility: "hidden" }),
        }),
      })}
      placeholder={isDisabled ? "" : t("greco.form.selectPlaceholder")}
      noOptionsMessage={() => t("greco.noResults")}
      isDisabled={isDisabled}
      isClearable
      ref={setRef(name) as any}
      inputId={name}
    />
  );

  return (
    <div
      style={{
        flex: 1,
      }}
    >
      <FieldContainer
        isTooltipHidden={isDisabled}
        tooltipText={label}
        isReadOnly={true}
      >
        {" "}
        <Label required={isRequired} disabled={isDisabled}>
          {t(label)}
        </Label>
        {renderField ? renderField({ field: fieldRender }) : fieldRender}
        {error && (
          <ErrorMessage id={field.name + "_error"}>{error}</ErrorMessage>
        )}
      </FieldContainer>{" "}
      <VSpace height={10} />
    </div>
  );
};
